import { isTagExtended, useTags } from "@/hooks/useTags";
import { getCurrentUrl } from "@/utils/getCurrentUrl";
import { getReadTime } from "@/utils/getReadTime";
import {
  ActionIcon,
  AspectRatio,
  Box,
  Button,
  Card,
  Group,
  Text,
} from "@mantine/core";
import { IconClockHour4, IconMessageCircle2 } from "@tabler/icons-react";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import relativeTime from "dayjs/plugin/relativeTime";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";
import * as React from "react";
import { BlogDataExtended } from "../Admin/Blog/interface";
import css from "./BlogArticle.module.css";
import { ImageBlock } from "./ImageBlock";

dayjs.locale("ru");
dayjs.extend(relativeTime);

const DynamicCommentCount = dynamic(
  () => import("disqus-react").then((mod) => mod.CommentCount),
  { ssr: false }
);

export function BlogArticle({
  images,
  tags,
  title,
  id,
  slug,
  json,
  created,
  description,
}: Partial<BlogDataExtended> & { id: string }) {
  const router = useRouter();

  const onClickBlog = () => {
    router.push(`/blog/${slug}`);
  };

  const { tags: tagsWithInfo } = useTags(tags);

  const createdDate = dayjs(created);

  const readTime = React.useMemo(() => getReadTime(json), [json]);

  return (
    <Card component="article" className={css.card}>
      <Link href={`/blog/${slug}`} prefetch={false}>
        <Text fz={24} fw={700}>
          {title}
        </Text>
      </Link>

      <Text fz="sm" className={css.description} lineClamp={4}>
        {description}
      </Text>

      {images && images.length > 0 && (
        <Card.Section px="lg" py="xs">
          <AspectRatio ratio={1920 / 1080} className={css.imageWrapper}>
            <ImageBlock images={images} onClick={onClickBlog} />
          </AspectRatio>
        </Card.Section>
      )}

      <Group justify="left" mt={8} gap="xs">
        {tagsWithInfo?.map((tag) => (
          <Button
            key={tag.value}
            variant="subtle"
            fz={12}
            onClick={() =>
              isTagExtended(tag) ? router.push(`/tag/${tag.value}`) : void 0
            }
            color="gray"
          >
            #{tag.label.toLowerCase()}
          </Button>
        ))}
      </Group>

      <Card.Section className={css.footer}>
        <Group justify="apart">
          <Group>
            <Text fz="sm">
              {createdDate.format("D MMMM YYYY")} ({createdDate.fromNow()})
            </Text>
            <Group gap="5px" title="Время чтения">
              <IconClockHour4 stroke={1} size={18} />
              <Text fz="sm">{readTime} мин</Text>
            </Group>

            <Link prefetch={false} href={`/blog/${slug}?comments`}>
              <ActionIcon
                size={18}
                variant="subtle"
                className={css.commentsButton}
              >
                <Box pos="absolute" left="15px" px={5} fz="sm">
                  <DynamicCommentCount
                    shortname="wofm-art"
                    config={{
                      url: `${getCurrentUrl()}/blog/${slug}`,
                      identifier: id,
                      title,
                    }}
                  />
                </Box>
                <IconMessageCircle2 />
              </ActionIcon>
            </Link>
          </Group>

          <Group>
            <Link href={`/blog/${slug}`} prefetch={false}>
              <Button variant="subtle">Читать далее</Button>
            </Link>
          </Group>
        </Group>
      </Card.Section>
    </Card>
  );
}
